import React, { useEffect, useMemo, useRef, useState, memo } from "react";
import userIcon from "../assets/user.png";
import Calender from "../assets/calendar.png";
import { Link, useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { addMonths } from "date-fns";
import { API_URL } from "../api";
import { getOptimizedCloudinaryUrl, getCloudinarySrcSet } from "../utils/cloudinary";

const carCardImageWidths = [320, 480, 640, 800];
const galleryImageWidths = [480, 720, 960, 1200];
const carCardImageSizes =
  "(max-width: 520px) 92vw, (max-width: 900px) 46vw, 360px";
const galleryImageSizes = "(max-width: 768px) 92vw, 900px";

function getCarImageUrl(car) {
  return getOptimizedCloudinaryUrl(car?.imageUrl);
}

function getCarImageSrcSet(car) {
  return getCloudinarySrcSet(car?.imageUrl, carCardImageWidths, 640 / 420);
}

function getGalleryImages(car) {
  const mainImage = car?.imageUrl
    ? [
        {
          imageKey: car.imagePublicId || "main-image",
          type: "exterior",
          src: getOptimizedCloudinaryUrl(car.imageUrl, 1200, 800),
          srcSet: getCloudinarySrcSet(car.imageUrl, galleryImageWidths, 1200 / 800),
        },
      ]
    : [];

  const galleryImages = Array.isArray(car?.images)
    ? car.images.map((image, index) => ({
        imageKey: image.publicId || image.url || `gallery-image-${index}`,
        type: image.type || "exterior",
        src: getOptimizedCloudinaryUrl(image.url, 1200, 800),
        srcSet: getCloudinarySrcSet(image.url, galleryImageWidths, 1200 / 800),
      }))
    : [];

  return [...mainImage, ...galleryImages].filter((image) => image.src);
}

function preloadImage(url) {
  if (!url || typeof document === "undefined") return;

  const existingPreload = document.querySelector(
    `link[rel="preload"][as="image"][href="${url}"]`,
  );
  if (existingPreload) return;

  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "image";
  link.href = url;
  link.fetchPriority = "high";
  document.head.appendChild(link);
}

function normalizeCarNamePart(value) {
  return String(value || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b(?:19|20)\d{2}\b/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function removeRepeatedCompanyName(companyName, carName) {
  const companyWords = normalizeCarNamePart(companyName)
    .split(" ")
    .filter(Boolean);
  const carWords = normalizeCarNamePart(carName).split(" ").filter(Boolean);

  if (!companyWords.length || !carWords.length) return carWords.join(" ");

  const startsWithCompany = companyWords.every(
    (word, index) => carWords[index]?.toLowerCase() === word.toLowerCase(),
  );

  return startsWithCompany
    ? carWords.slice(companyWords.length).join(" ")
    : carWords.join(" ");
}

export function getCarDisplayName(car) {
  const companyName = normalizeCarNamePart(car.companyName);
  const carName = removeRepeatedCompanyName(companyName, car.name);

  return [companyName, carName].filter(Boolean).join(" ");
}

function normalizeCarType(type) {
  return String(type || "").toLowerCase();
}

function formatCarDate(date) {
  if (!date) return "";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatBookingDate(date) {
  if (!date) return "";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function mergeDatePart(currentDate, nextDate) {
  if (!nextDate) return null;

  const mergedDate = new Date(nextDate);
  if (currentDate) {
    mergedDate.setHours(currentDate.getHours(), currentDate.getMinutes(), 0, 0);
  }
  return mergedDate;
}

function mergeTimePart(currentDate, nextTime) {
  if (!nextTime) return null;

  const mergedDate = currentDate ? new Date(currentDate) : new Date();
  mergedDate.setHours(nextTime.getHours(), nextTime.getMinutes(), 0, 0);
  return mergedDate;
}

const GalleryThumbnail = memo(({ image, index, isActive, onClick }) => (
  <button
    type="button"
    className={index === isActive ? "activeThumb" : ""}
    onClick={onClick}
  >
    <img
      src={getOptimizedCloudinaryUrl(image.src, 160, 107)}
      alt={image.type}
      width="160"
      height="107"
      loading="lazy"
      decoding="async"
    />
  </button>
));

GalleryThumbnail.displayName = "GalleryThumbnail";

const GalleryImage = memo(({ src, srcSet, sizes, alt, width, height }) => (
  <img
    src={src}
    srcSet={srcSet}
    sizes={sizes}
    alt={alt}
    width={width}
    height={height}
    decoding="async"
  />
));

GalleryImage.displayName = "GalleryImage";

function CarList() {
  const [cars, setCars] = useState([]);
  const [carsError, setCarsError] = useState("");
  const [isLoadingCars, setIsLoadingCars] = useState(true);
  const [selectedType, setSelectedType] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [clickedCarId, setClickedCarId] = useState(null);
  const [selectedRent, setSelectedRent] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [returnDate, setReturnDate] = useState(null);
  const [bookingDateError, setBookingDateError] = useState("");
  const [carAlert, setCarAlert] = useState(null);
  const scrollToRef = useRef(null);
  const galleryTouchStartRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    setIsLoadingCars(true);
    setCarsError("");

    fetch(`${API_URL}/cars`, { cache: "no-store" })
      .then((response) => {
        if (!response.ok) throw new Error("Could not load cars");
        return response.json();
      })
      .then((data) => {
        const carArray = Array.isArray(data) ? data : data.cars || [];
        setCars(
          carArray.map((car) => ({
            ...car,
            type: normalizeCarType(car.type),
            types: Array.isArray(car.types)
              ? car.types.map(normalizeCarType)
              : car.types,
          })),
        );
      })
      .catch(() => {
        setCars([]);
        setCarsError(
          "Could not load cars. Please make sure the backend server is running.",
        );
      })
      .finally(() => {
        setIsLoadingCars(false);
      });
  }, []);

  useEffect(() => {
    cars.slice(0, 1).forEach((car) => preloadImage(getCarImageUrl(car)));
  }, [cars]);

  useEffect(() => {
    const clearSelectedCar = (event) => {
      if (event.target.closest(".carCardContainer")) return;
      if (event.target.closest(".galleryModal")) return;
      setClickedCarId(null);
    };

    document.addEventListener("mousedown", clearSelectedCar);
    return () => document.removeEventListener("mousedown", clearSelectedCar);
  }, []);

  const filteredCars = useMemo(() => {
    const typeMatchingCars =
      selectedType === "all"
        ? cars
        : cars.filter(
            (car) =>
              car.type === selectedType ||
              (Array.isArray(car.types) && car.types.includes(selectedType)),
          );

    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return typeMatchingCars;

    return typeMatchingCars.filter((car) =>
      getCarDisplayName(car).toLowerCase().includes(normalizedSearch),
    );
  }, [cars, selectedType, searchTerm]);

  const findCar = (car) => {
    if (car.available === false) return;

    const carId = car._id || car.id || car.legacyId;
    setClickedCarId(carId);
    setCarAlert(null);
  };

  const chooseRent = (event, car, period) => {
    event.stopPropagation();
    if (car.available === false) return;

    const carId = car._id || car.id || car.legacyId;
    setClickedCarId(carId);
    setSelectedRent({
      carId,
      period,
      price: car.rent[period],
    });
    setCarAlert(null);
  };

  const openGallery = (event, car) => {
    event.stopPropagation();
    const images = getGalleryImages(car);
    if (!images.length) return;

    setGallery({
      car,
      images,
      activeIndex: 0,
    });
  };

  const closeGallery = () => setGallery(null);

  const showPreviousImage = (event) => {
    event.stopPropagation();
    setGallery((currentGallery) => ({
      ...currentGallery,
      activeIndex:
        (currentGallery.activeIndex - 1 + currentGallery.images.length) %
        currentGallery.images.length,
    }));
  };

  const showNextImage = (event) => {
    event.stopPropagation();
    setGallery((currentGallery) => ({
      ...currentGallery,
      activeIndex:
        (currentGallery.activeIndex + 1) % currentGallery.images.length,
    }));
  };

  const handleGalleryTouchStart = (event) => {
    if (!gallery || gallery.images.length < 2) return;

    const touch = event.touches[0];
    galleryTouchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
    };
  };

  const handleGalleryTouchEnd = (event) => {
    if (
      !galleryTouchStartRef.current ||
      !gallery ||
      gallery.images.length < 2
    ) {
      galleryTouchStartRef.current = null;
      return;
    }

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - galleryTouchStartRef.current.x;
    const deltaY = touch.clientY - galleryTouchStartRef.current.y;
    galleryTouchStartRef.current = null;

    if (Math.abs(deltaX) < 45 || Math.abs(deltaX) < Math.abs(deltaY)) return;

    setGallery((currentGallery) => ({
      ...currentGallery,
      activeIndex:
        deltaX < 0
          ? (currentGallery.activeIndex + 1) % currentGallery.images.length
          : (currentGallery.activeIndex - 1 + currentGallery.images.length) %
            currentGallery.images.length,
    }));
  };

  const handlePickupDateChange = (date) => {
    const nextStartDate = mergeDatePart(startDate, date);
    setStartDate(nextStartDate);
    if (returnDate && nextStartDate && returnDate < nextStartDate) {
      setReturnDate(null);
    }
    setBookingDateError("");
  };

  const handlePickupTimeChange = (time) => {
    const nextStartDate = mergeTimePart(startDate, time);
    setStartDate(nextStartDate);
    if (returnDate && nextStartDate && returnDate < nextStartDate) {
      setReturnDate(null);
    }
    setBookingDateError("");
  };

  const handleReturnDateChange = (date) => {
    setReturnDate(mergeDatePart(returnDate, date));
    setBookingDateError("");
  };

  const handleReturnTimeChange = (time) => {
    setReturnDate(mergeTimePart(returnDate, time));
    setBookingDateError("");
  };

  const isBookingSelectionComplete = (carId) => {
    if (!startDate) {
      setBookingDateError("Please choose the pickup date and time first.");
      scrollToRef.current?.scrollIntoView({ behavior: "smooth" });
      return false;
    }

    if (!returnDate) {
      setBookingDateError("Please choose the return date and time first.");
      scrollToRef.current?.scrollIntoView({ behavior: "smooth" });
      return false;
    }

    if (selectedRent?.carId !== carId) {
      setClickedCarId(carId);
      setCarAlert({
        carId,
        message: "Please choose Daily or Monthly first.",
      });
      return false;
    }

    return true;
  };

  const handleWhatsAppClick = (event, carId) => {
    event.stopPropagation();
    if (!isBookingSelectionComplete(carId)) event.preventDefault();
  };

  const handlePayNowClick = (event, carId) => {
    event.stopPropagation();
    if (!isBookingSelectionComplete(carId)) return;

    const params = new URLSearchParams({
      period: selectedRent.period,
      pickup: startDate.toISOString(),
      return: returnDate.toISOString(),
    });
    window.scrollTo(0, 0);
    navigate(`/booking/${carId}?${params.toString()}`);
  };

  const phoneNumber = "+971529497677";
  const getBookingMessage = (car, carId) => {
    const carTitle = getCarDisplayName(car);
    const rentText =
      selectedRent?.carId === carId
        ? `, ${selectedRent.period} rent: AED ${selectedRent.price}`
        : "";
    const bookingDateText = startDate
      ? `, pickup date: ${formatBookingDate(startDate)}`
      : "";
    const returnDateText = returnDate
      ? `, return date: ${formatBookingDate(returnDate)}`
      : "";

    return `Hello, I want to Book ${carTitle}${rentText}${bookingDateText}${returnDateText}`;
  };

  return (
    <>
      <div className="DatePickContainer" ref={scrollToRef}>
        <div className="DatePick">
          <img
            src={Calender}
            alt="calender"
            width="30px"
            height="30"
            style={{ marginRight: "10px" }}
          />
          <DatePicker
            selected={startDate}
            onChange={handlePickupDateChange}
            dateFormat="d.MMMM"
            isClearable
            placeholderText="Pickup date"
            minDate={new Date()}
            maxDate={addMonths(new Date(), 5)}
            className="PickerDate"
            withPortal
          />
          <DatePicker
            selected={startDate}
            onChange={handlePickupTimeChange}
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={15}
            timeCaption="Time"
            dateFormat="h:mm aa"
            className="PickerTime"
            placeholderText="Pickup time"
            withPortal
          />
        </div>
        <div className="DatePick">
          <img
            src={Calender}
            alt="calender"
            width="30px"
            height="30"
            style={{ marginRight: "10px" }}
          />
          <DatePicker
            selected={returnDate}
            onChange={handleReturnDateChange}
            dateFormat="d.MMMM"
            isClearable
            placeholderText="Return date"
            showDisabledMonthNavigation
            minDate={startDate || new Date()}
            maxDate={addMonths(new Date(), 5)}
            className="PickerDate"
            withPortal
          />
          <DatePicker
            selected={returnDate}
            onChange={handleReturnTimeChange}
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={15}
            timeCaption="Time"
            dateFormat="h:mm aa"
            className="PickerTime"
            placeholderText="Return time"
            withPortal
          />
        </div>
        {bookingDateError && (
          <p className="bookingDateError">{bookingDateError}</p>
        )}
        <p className="bookingTermsNote">
          By booking, you agree to our{" "}
          <Link to="/terms" onClick={() => window.scrollTo(0, 0)}>Terms & Conditions</Link>.
        </p>
      </div>

      <div className="carsControls">
        <div className="carsSearch">
          <label htmlFor="car-search">Search cars</label>
          <input
            id="car-search"
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search by car name"
          />
        </div>

        <div className="cntr" aria-label="Filter cars by category">
          {["all", "sedan", "suv", "sport", "luxury"].map((type) => (
            <label
              className={`radio ${selectedType === type ? "radioActive" : ""}`}
              key={type}
            >
              <input
                type="radio"
                value={type}
                checked={selectedType === type}
                onChange={(event) => setSelectedType(event.target.value)}
                className="hidden"
              />
              <span className="label"></span>
              {type === "all" ? "All" : type.toUpperCase()}
            </label>
          ))}
        </div>
      </div>

      <div className="CarList">
        {carsError && <p className="carsError">{carsError}</p>}
        {isLoadingCars && !carsError && (
          <div className="carsSkeletonGrid" aria-hidden="true">
            {[1, 2, 3, 4].map((item) => (
              <div className="carSkeletonCard" key={item}>
                <div className="carSkeletonImage" />
                <div className="carSkeletonDetails">
                  <span />
                  <span />
                </div>
                <div className="carSkeletonTitle" />
              </div>
            ))}
          </div>
        )}
        {!isLoadingCars &&
          !carsError &&
          !filteredCars.length &&
          (searchTerm || selectedType !== "all") && (
            <p className="carsEmpty">
              No matching cars. Try another search or category.
            </p>
          )}
        {!isLoadingCars &&
          filteredCars.map((car, index) => {
            const carId = car._id || car.id || car.legacyId;
            const galleryImages = getGalleryImages(car);
            const isUnavailable = car.available === false;
            const carDisplayName = getCarDisplayName(car);

            return (
              <div
                key={carId}
                style={{ position: "relative" }}
                className={`carCardContainer ${
                  clickedCarId === carId ? "click" : ""
                } ${isUnavailable ? "notAvailable" : ""}`}
                onClick={() => findCar(car)}
              >
                {carAlert?.carId === carId && (
                  <div className="carInlineAlert" role="alert">
                    <div>
                      <strong>Rent option required</strong>
                      <span>{carAlert.message}</span>
                    </div>
                    <button
                      type="button"
                      aria-label="Close alert"
                      onClick={(event) => {
                        event.stopPropagation();
                        setCarAlert(null);
                      }}
                    >
                      x
                    </button>
                  </div>
                )}
                <div
                  className="imgCardDiv"
                  onClick={(event) => openGallery(event, car)}
                >
                  {isUnavailable && (
                    <span className="availabilityBadge">Unavailable</span>
                  )}
                  <img
                    className="carImgCard"
                    src={getCarImageUrl(car)}
                    srcSet={getCarImageSrcSet(car)}
                    sizes={carCardImageSizes}
                    alt={carDisplayName}
                    width="640"
                    height="420"
                    loading={index === 0 ? "eager" : "lazy"}
                    fetchPriority={index === 0 ? "high" : "auto"}
                    decoding="async"
                  />
                  {galleryImages.length > 1 && (
                    <button type="button" className="galleryCount">
                      View photos
                    </button>
                  )}
                </div>
                <div className="detailsDiv">
                  <div className="priceGroup">
                    <button
                      type="button"
                      className={`priceItem ${
                        selectedRent?.carId === carId &&
                        selectedRent?.period === "daily"
                          ? "priceItemSelected"
                          : ""
                      }`}
                      disabled={isUnavailable}
                      onClick={(event) => chooseRent(event, car, "daily")}
                    >
                      <span className="priceLabel">Daily</span>
                      <span className="priceValue">AED {car.rent.daily}</span>
                    </button>
                    <button
                      type="button"
                      className={`priceItem ${
                        selectedRent?.carId === carId &&
                        selectedRent?.period === "monthly"
                          ? "priceItemSelected"
                          : ""
                      }`}
                      disabled={isUnavailable}
                      onClick={(event) => chooseRent(event, car, "monthly")}
                    >
                      <span className="priceLabel">Monthly</span>
                      <span className="priceValue">AED {car.rent.monthly}</span>
                    </button>
                  </div>
                  <p className="carCapacityCard">
                    <img src={userIcon} alt="user" width="14px" />
                    {car.capacity}
                  </p>
                  {isUnavailable && (
                    <p className="unavailableMessage">
                      Unavailable
                      {car.availableFrom &&
                        ` from ${formatCarDate(car.availableFrom)}`}
                      {car.availableFrom && car.availableTo && " to"}
                      {car.availableTo && ` ${formatCarDate(car.availableTo)}`}
                    </p>
                  )}
                </div>
                <div className="ButtonsCardDiv">
                  <div className="headerCardDiv">
                    <h2
                      className={`carNameCard ${
                        clickedCarId === carId ? "carNameCardSelected" : ""
                      }`}
                    >
                      <span>{carDisplayName}</span>
                      <small className="carSimilarNote">or similar</small>
                    </h2>
                  </div>
                  <div className="whatsAppDiv">
                    {isUnavailable ? (
                      <button
                        type="button"
                        className="button1 buttonDisabled"
                        disabled
                      >
                        <svg
                          className="whatsAppIcon"
                          viewBox="0 0 32 32"
                          aria-hidden="true"
                        >
                          <path d="M16.02 3.2C9 3.2 3.3 8.9 3.3 15.9c0 2.2.6 4.4 1.7 6.3L3.2 28.8l6.8-1.8c1.8 1 3.9 1.5 6 1.5 7 0 12.7-5.7 12.7-12.7S23 3.2 16.02 3.2Zm0 22.9c-1.9 0-3.7-.5-5.3-1.5l-.4-.2-4 1.1 1.1-3.9-.3-.4c-1.1-1.6-1.6-3.5-1.6-5.4 0-5.8 4.7-10.4 10.5-10.4s10.5 4.7 10.5 10.4c0 5.8-4.7 10.5-10.5 10.5Zm5.7-7.8c-.3-.2-1.9-.9-2.2-1-.3-.1-.5-.2-.7.2-.2.3-.8 1-.9 1.1-.2.2-.3.2-.6.1-.3-.2-1.3-.5-2.5-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6.1-.1.3-.3.5-.5.2-.2.2-.3.3-.5.1-.2 0-.4 0-.5-.1-.2-.7-1.7-1-2.3-.3-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.4s1.1 2.8 1.2 3c.2.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.9-.8 2.1-1.5.3-.7.3-1.3.2-1.5-.1-.1-.3-.2-.6-.4Z" />
                        </svg>
                        <span>Unavailable</span>
                      </button>
                    ) : (
                      <a
                        className="button1"
                        href={`https://api.whatsapp.com/send?phone=${phoneNumber}&text=${encodeURIComponent(
                          getBookingMessage(car, carId),
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(event) => handleWhatsAppClick(event, carId)}
                      >
                        <svg
                          className="whatsAppIcon"
                          viewBox="0 0 32 32"
                          aria-hidden="true"
                        >
                          <path d="M16.02 3.2C9 3.2 3.3 8.9 3.3 15.9c0 2.2.6 4.4 1.7 6.3L3.2 28.8l6.8-1.8c1.8 1 3.9 1.5 6 1.5 7 0 12.7-5.7 12.7-12.7S23 3.2 16.02 3.2Zm0 22.9c-1.9 0-3.7-.5-5.3-1.5l-.4-.2-4 1.1 1.1-3.9-.3-.4c-1.1-1.6-1.6-3.5-1.6-5.4 0-5.8 4.7-10.4 10.5-10.4s10.5 4.7 10.5 10.4c0 5.8-4.7 10.5-10.5 10.5Zm5.7-7.8c-.3-.2-1.9-.9-2.2-1-.3-.1-.5-.2-.7.2-.2.3-.8 1-.9 1.1-.2.2-.3.2-.6.1-.3-.2-1.3-.5-2.5-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6.1-.1.3-.3.5-.5.2-.2.2-.3.3-.5.1-.2 0-.4 0-.5-.1-.2-.7-1.7-1-2.3-.3-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.4s1.1 2.8 1.2 3c.2.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.9-.8 2.1-1.5.3-.7.3-1.3.2-1.5-.1-.1-.3-.2-.6-.4Z" />
                        </svg>
                        <span>Book on WhatsApp</span>
                      </a>
                    )}
                    {!isUnavailable && (
                      <button
                        type="button"
                        className="payNowButton"
                        onClick={(event) => handlePayNowClick(event, carId)}
                      >
                        Pay now
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
      </div>
      {gallery && (
        <div className="galleryOverlay" onClick={closeGallery}>
          <div
            className="galleryModal"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="galleryClose"
              onClick={closeGallery}
            >
              Close
            </button>
            <div className="galleryHeader">
              <h2>{getCarDisplayName(gallery.car)}</h2>
              <span>
                {gallery.activeIndex + 1} / {gallery.images.length}
              </span>
            </div>
            <div
              className="galleryImageWrap"
              onTouchStart={handleGalleryTouchStart}
              onTouchEnd={handleGalleryTouchEnd}
            >
              {gallery.images.length > 1 && (
                <img
                  className="galleryImageBackdrop"
                  src={gallery.images[gallery.activeIndex].src}
                  srcSet={gallery.images[gallery.activeIndex].srcSet}
                  sizes={galleryImageSizes}
                  alt=""
                  aria-hidden="true"
                  decoding="async"
                />
              )}
              {gallery.images.length > 1 && (
                <button
                  type="button"
                  className="galleryArrow galleryArrowLeft"
                  onClick={showPreviousImage}
                >
                  ‹
                </button>
              )}
              <GalleryImage
                src={gallery.images[gallery.activeIndex].src}
                srcSet={gallery.images[gallery.activeIndex].srcSet}
                sizes={galleryImageSizes}
                alt={getCarDisplayName(gallery.car)}
                width="1200"
                height="800"
                decoding="async"
              />
              {gallery.images.length > 1 && (
                <button
                  type="button"
                  className="galleryArrow galleryArrowRight"
                  onClick={showNextImage}
                >
                  ›
                </button>
              )}
            </div>
            <div className="galleryThumbs">
              {gallery.images.map((image, index) => (
                <GalleryThumbnail
                  key={image.imageKey}
                  image={image}
                  index={index}
                  isActive={gallery.activeIndex}
                  onClick={() =>
                    setGallery((currentGallery) => ({
                      ...currentGallery,
                      activeIndex: index,
                    }))
                  }
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CarList;
